"use client";

import React from "react";
import { useCreatePageStore } from "@/stores/createPageStore";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * Workspace errors remain visible in Chat without covering its controls.
 * `restore_conflict:` errors additionally offer
 * "Load saved version" / "Keep this draft" recovery actions.
 */
export default function ChatErrorBanner({
  onQuoteRetry,
}: {
  onQuoteRetry?: () => void;
}) {
  const conversationError = useCreatePageStore(
    state => state.agent.conversation.error
  );
  const ticketSubmissionError = useCreatePageStore(
    state => state.ticket.submissionError
  );
  const generationError = useCreatePageStore(
    state => state.generation.errors.message
  );

  const errors = [
    ...new Set(
      [conversationError, ticketSubmissionError, generationError].filter(
        (error): error is string => Boolean(error)
      )
    ),
  ];
  if (!errors.length) {
    return null;
  }

  return (
    <>
      {errors.map(error => {
        const isRestoreConflict = error.startsWith("restore_conflict:");
        const displayError = isRestoreConflict
          ? error.replace("restore_conflict:", "").trim()
          : error;
        return (
          <Alert
            key={error}
            variant="error"
            data-slot={
              error === generationError ? "chat-generation-error" : "chat-error"
            }
          >
            <AlertDescription>
              {displayError}
              {error === ticketSubmissionError && onQuoteRetry && (
                <div className="mt-2">
                  <Button size="sm" variant="outline" onClick={onQuoteRetry}>
                    Try your free quote again
                  </Button>
                </div>
              )}
              {isRestoreConflict && (
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.location.reload()}
                  >
                    Load saved version
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      useCreatePageStore.getState().setAgentError(null)
                    }
                  >
                    Keep this draft
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        );
      })}
    </>
  );
}
